"use strict";
import { call, delay, select } from "typed-redux-saga";
import { getNativeAddress } from "constants/uniswapAddresses";
import {
  PortfolioBalancesDocument
} from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { GQLQueries } from "data/graphql/uniswap-data-api/queries";
import { GQL_MAINNET_CHAINS, GQL_TESTNET_CHAINS } from "features/chains/chainInfo";
import { fromGraphQLChain } from "features/chains/utils";
import { DynamicConfigs, NetworkRequestsConfigKey } from "features/gating/configs";
import { getDynamicConfigValue } from "features/gating/hooks";
import { GQL_QUERIES_TO_REFETCH_ON_TXN_UPDATE } from "features/portfolio/portfolioUpdates/constants";
import { getCurrenciesWithExpectedUpdates } from "features/portfolio/portfolioUpdates/getCurrenciesWithExpectedUpdates";
import { selectIsTestnetModeEnabled } from "features/settings/selectors";
import { WalletEventName } from "features/telemetry/constants";
import { sendAnalyticsEvent } from "features/telemetry/send";
import { buildCurrencyId } from "utils/uniswapCurrencyId";
import { logger } from "utilities/src/logger/logger";
import { ONE_SECOND_MS } from "utilities/src/time/time";
const REFETCH_INTERVAL = ONE_SECOND_MS * 3;
const MAX_REFETCH_ATTEMPTS_FALLBACK = 30;
export function* refetchGQLQueriesViaBackendPollVariant({
  transaction,
  apolloClient,
  activeAddress
}) {
  const owner = transaction.from;
  const isTestnetMode = yield* select(selectIsTestnetModeEnabled);
  const currenciesWithBalToUpdate = getCurrenciesWithExpectedUpdates(transaction);
  const currencyIdToStartingBalance = readBalancesFromCache({
    owner,
    currencyIds: currenciesWithBalToUpdate,
    apolloClient,
    isTestnetMode
  });
  const maxRefetchAttempts = getDynamicConfigValue(
    DynamicConfigs.NetworkRequests,
    NetworkRequestsConfigKey.BalanceMaxRefetchAttempts,
    MAX_REFETCH_ATTEMPTS_FALLBACK
  );
  if (owner !== activeAddress) {
    return;
  }
  yield* delay(REFETCH_INTERVAL);
  yield* call([apolloClient, apolloClient.refetchQueries], { include: GQL_QUERIES_TO_REFETCH_ON_TXN_UPDATE });
  if (!currencyIdToStartingBalance) {
    return;
  }
  let freshnessLag = REFETCH_INTERVAL;
  let i = 0;
  let lastUpdatedBalances;
  while (i < maxRefetchAttempts) {
    const currencyIdToUpdatedBalance = readBalancesFromCache({
      owner,
      currencyIds: currenciesWithBalToUpdate,
      apolloClient,
      isTestnetMode
    });
    lastUpdatedBalances = currencyIdToUpdatedBalance;
    if (checkIfBalancesUpdated(currencyIdToStartingBalance, currencyIdToUpdatedBalance)) {
      break;
    }
    yield* delay(REFETCH_INTERVAL);
    const currentActiveAddress = activeAddress;
    if (owner !== currentActiveAddress) {
      break;
    }
    yield* call([apolloClient, apolloClient.refetchQueries], {
      include: [GQLQueries.PortfolioBalances]
    });
    freshnessLag += REFETCH_INTERVAL;
    i += 1;
  }
  if (i >= 10) {
    logger.info("refetchGQLQueriesSaga", "refetchGQLQueries", "Large balance freshness lag", {
      iterations: i,
      startingBalances: currencyIdToStartingBalance,
      lastUpdatedBalances,
      currencyIds: currenciesWithBalToUpdate
    });
  }
  sendAnalyticsEvent(WalletEventName.PortfolioBalanceFreshnessLag, {
    freshnessLag,
    updatedCurrencies: Object.keys(currencyIdToStartingBalance)
  });
}
function readBalancesFromCache({
  owner,
  currencyIds,
  apolloClient,
  isTestnetMode
}) {
  if (!currencyIds?.size) {
    return void 0;
  }
  const currencyIdsToUpdate = new Set(currencyIds);
  const currencyIdToBalance = Array.from(currencyIdsToUpdate).reduce(
    (currIdToBal, currencyId) => ({ ...currIdToBal, [currencyId]: 0 }),
    // assume 0 balance and update later if found in cache
    {}
  );
  const chains = isTestnetMode ? GQL_TESTNET_CHAINS : GQL_MAINNET_CHAINS;
  const cachedBalancesData = apolloClient.readQuery({
    query: PortfolioBalancesDocument,
    variables: { ownerAddress: owner, chains }
  });
  if (!cachedBalancesData) {
    logger.info("refetchGQLQueriesSaga", "readBalancesFromCache", "No cached balances data", {
      currencyIds: currencyIdsToUpdate
    });
  }
  for (const tokenData of cachedBalancesData?.portfolios?.[0]?.tokenBalances ?? []) {
    const chainId = fromGraphQLChain(tokenData?.token?.chain);
    if (!chainId) {
      continue;
    }
    const tokenAddress = tokenData?.token?.address ?? getNativeAddress(chainId);
    const currencyId = buildCurrencyId(chainId, tokenAddress).toLowerCase();
    if (currencyIdsToUpdate.has(currencyId)) {
      currencyIdsToUpdate.delete(currencyId);
      currencyIdToBalance[currencyId] = tokenData?.quantity ?? 0;
    }
    if (!currencyIdsToUpdate.size) {
      break;
    }
  }
  return currencyIdToBalance;
}
function checkIfBalancesUpdated(balance1, balance2) {
  if (!balance2) {
    return true;
  }
  const currencyIds = Object.keys(balance1);
  for (const currencyId of currencyIds) {
    if (balance1[currencyId] === balance2[currencyId]) {
      return false;
    }
  }
  return true;
}
